import ExcelJS from "exceljs";

function downloadWorkbook(workbook, filename) {
  return workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return filename;
  });
}

function writeKvSheet(ws, title, subtitle, rows) {
  ws.getCell(1, 1).value = title;
  ws.getCell(1, 1).font = { bold: true, size: 14 };
  ws.getCell(2, 1).value = subtitle;
  ws.getCell(2, 1).font = { size: 10, color: { argb: "FF6B7280" } };
  ws.getCell(4, 1).value = "Metric";
  ws.getCell(4, 2).value = "Value";
  ws.getCell(4, 1).font = { bold: true };
  ws.getCell(4, 2).font = { bold: true };
  let r = 5;
  (rows || []).forEach(([k, v]) => {
    ws.getCell(r, 1).value = k;
    ws.getCell(r, 2).value = v;
    r += 1;
  });
  ws.getColumn(1).width = 28;
  ws.getColumn(2).width = 18;
}

/**
 * Employee (Workforce) Dashboard Excel — pulse + masters + alerts.
 */
export async function exportEmployeeDashboardExcel({
  metrics = {},
  masters = [],
  alerts = [],
  movement = null,
  tenure = [],
  compare = [],
  periodLabel = "",
  groupBy = "department",
  cardDetail = null,
} = {}) {
  if (cardDetail) {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Detail");
    writeKvSheet(
      ws,
      cardDetail.title || "Card detail",
      cardDetail.periodLabel ? `Period: ${cardDetail.periodLabel}` : "",
      cardDetail.summary || []
    );
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = String(cardDetail.title || "card")
      .replace(/[^a-zA-Z0-9_]+/g, "_")
      .slice(0, 30);
    return downloadWorkbook(wb, `Workforce_Card_${slug}_${stamp}.xlsx`);
  }

  const wb = new ExcelJS.Workbook();
  const pulse = wb.addWorksheet("Pulse");
  writeKvSheet(pulse, "Workforce Dashboard", `Period: ${periodLabel}`, [
    ["Headcount", metrics.headcount],
    ["Active", metrics.active],
    ["New joinees", metrics.newJoinees],
    ["New joinee rate %", metrics.newJoineeRate],
    ["Attrition", metrics.attrition],
    ["Attrition rate %", metrics.attritionRate],
    ["Men", metrics.male],
    ["Women", metrics.female],
    ["M:F ratio", metrics.genderRatio],
    ["Probation", metrics.probation],
    ["Confirmed", metrics.confirmed],
    ["Full-time", metrics.fullTime],
    ["External", metrics.external],
    ["Relieved", metrics.relieved],
    ["Resigned", metrics.resigned],
    ["Terminated", metrics.terminated],
  ]);

  const masterWs = wb.addWorksheet("By master");
  masterWs.getCell(1, 1).value = `Active HC by ${groupBy}`;
  masterWs.getCell(1, 1).font = { bold: true, size: 12 };
  masterWs.getCell(3, 1).value = "Label";
  masterWs.getCell(3, 2).value = "Count";
  masterWs.getCell(3, 1).font = { bold: true };
  masterWs.getCell(3, 2).font = { bold: true };
  (masters || []).forEach((m, i) => {
    masterWs.getCell(4 + i, 1).value = m.label;
    masterWs.getCell(4 + i, 2).value = m.value;
  });
  masterWs.getColumn(1).width = 28;
  masterWs.getColumn(2).width = 12;

  if (alerts?.length) {
    const aWs = wb.addWorksheet("Alerts");
    aWs.getCell(1, 1).value = "Alerts";
    aWs.getCell(1, 1).font = { bold: true };
    aWs.getCell(3, 1).value = "Title";
    aWs.getCell(3, 2).value = "Detail";
    alerts.forEach((a, i) => {
      aWs.getCell(4 + i, 1).value = a.title;
      aWs.getCell(4 + i, 2).value = a.detail;
    });
    aWs.getColumn(1).width = 28;
    aWs.getColumn(2).width = 60;
  }

  if (movement?.labels?.length) {
    const tWs = wb.addWorksheet("Movements");
    tWs.getCell(1, 1).value = `Joins vs exits — ${movement.year}`;
    tWs.getCell(1, 1).font = { bold: true };
    tWs.getCell(3, 1).value = "Month";
    tWs.getCell(3, 2).value = "Joins";
    tWs.getCell(3, 3).value = "Exits";
    movement.labels.forEach((lab, i) => {
      tWs.getCell(4 + i, 1).value = lab;
      tWs.getCell(4 + i, 2).value = movement.joins[i] || 0;
      tWs.getCell(4 + i, 3).value = movement.exits[i] || 0;
    });
  }

  if (tenure?.length) {
    const tWs = wb.addWorksheet("Tenure");
    tWs.getCell(1, 1).value = "Tenure mix (active)";
    tWs.getCell(1, 1).font = { bold: true, size: 12 };
    tWs.getCell(3, 1).value = "Bucket";
    tWs.getCell(3, 2).value = "Count";
    tWs.getCell(3, 1).font = { bold: true };
    tWs.getCell(3, 2).font = { bold: true };
    tenure.forEach((t, i) => {
      tWs.getCell(4 + i, 1).value = t.label;
      tWs.getCell(4 + i, 2).value = t.value;
    });
    tWs.getColumn(1).width = 28;
    tWs.getColumn(2).width = 12;
  }

  if (compare?.length) {
    const cWs = wb.addWorksheet("Compare");
    cWs.getCell(1, 1).value = "Month comparison";
    cWs.getCell(1, 1).font = { bold: true, size: 12 };
    cWs.getCell(3, 1).value = "Month";
    cWs.getCell(3, 2).value = "Active";
    cWs.getCell(3, 3).value = "Joins";
    cWs.getCell(3, 4).value = "Exits";
    cWs.getCell(3, 5).value = "Net";
    cWs.getCell(3, 6).value = "Men";
    cWs.getCell(3, 7).value = "Women";
    cWs.getCell(3, 8).value = "Probation";
    [1, 2, 3, 4, 5, 6, 7, 8].forEach((col) => {
      cWs.getCell(3, col).font = { bold: true };
    });
    compare.forEach((c, i) => {
      cWs.getCell(4 + i, 1).value = c.payMonth || c.label;
      cWs.getCell(4 + i, 2).value = c.active;
      cWs.getCell(4 + i, 3).value = c.joins;
      cWs.getCell(4 + i, 4).value = c.exits;
      cWs.getCell(4 + i, 5).value = c.netChange;
      cWs.getCell(4 + i, 6).value = c.male;
      cWs.getCell(4 + i, 7).value = c.female;
      cWs.getCell(4 + i, 8).value = c.probation;
    });
  }

  const stamp = new Date().toISOString().slice(0, 10);
  return downloadWorkbook(wb, `Workforce_Dashboard_${stamp}.xlsx`);
}

/**
 * Attendance Dashboard Excel — pulse + ranks + leave mix.
 */
export async function exportAttendanceDashboardExcel({
  metrics = {},
  periodLabel = "",
  compare = [],
  processRows = [],
} = {}) {
  const wb = new ExcelJS.Workbook();
  const pulse = wb.addWorksheet("Pulse");
  writeKvSheet(pulse, "Attendance Dashboard", `Period: ${periodLabel}`, [
    ["Headcount in view", metrics.headcount],
    ["Employees with data", metrics.employeesWithData],
    ["Present days", metrics.totalPresentDays],
    ["Absent / LOP days", metrics.totalAbsentDays],
    ["Leave days", metrics.totalLeaveDays],
    ["WO days", metrics.totalWoDays],
    ["PH days", metrics.totalPhDays],
    ["Present rate %", metrics.presentRate],
    ["Absenteeism rate %", metrics.absenteeismRate],
    ["Avg present / emp", metrics.avgPresentPerEmployee],
    ["Pending leave requests", metrics.pendingLeaveRequests],
    ["Regularisation signals", metrics.regularizationChecks],
  ]);

  const leaveWs = wb.addWorksheet("Leave mix");
  leaveWs.getCell(1, 1).value = "Leave code";
  leaveWs.getCell(1, 2).value = "Days";
  (metrics.leaveRows || []).forEach((r, i) => {
    leaveWs.getCell(2 + i, 1).value = r.code;
    leaveWs.getCell(2 + i, 2).value = r.count;
  });

  const absWs = wb.addWorksheet("Top absent depts");
  absWs.getCell(1, 1).value = "Department";
  absWs.getCell(1, 2).value = "Absent days";
  (metrics.topAbsentDepts || []).forEach((r, i) => {
    absWs.getCell(2 + i, 1).value = r.name;
    absWs.getCell(2 + i, 2).value = r.count;
  });

  if (metrics.alerts?.length) {
    const aWs = wb.addWorksheet("Alerts");
    aWs.getCell(1, 1).value = "Title";
    aWs.getCell(1, 2).value = "Detail";
    metrics.alerts.forEach((a, i) => {
      aWs.getCell(2 + i, 1).value = a.title;
      aWs.getCell(2 + i, 2).value = a.detail;
    });
  }

  const compareRows = compare?.length ? compare : processRows;
  if (compareRows?.length) {
    const cWs = wb.addWorksheet("Compare");
    cWs.getCell(1, 1).value = "Month comparison (process status)";
    cWs.getCell(1, 1).font = { bold: true, size: 12 };
    cWs.getCell(3, 1).value = "Month";
    cWs.getCell(3, 2).value = "Scope";
    cWs.getCell(3, 3).value = "Processed";
    cWs.getCell(3, 4).value = "Open";
    cWs.getCell(3, 5).value = "Locked";
    cWs.getCell(3, 6).value = "Present %";
    cWs.getCell(3, 7).value = "Absent days";
    [1, 2, 3, 4, 5, 6, 7].forEach((col) => {
      cWs.getCell(3, col).font = { bold: true };
    });
    compareRows.forEach((r, i) => {
      const row = 4 + i;
      cWs.getCell(row, 1).value = r.label || r.payMonth;
      cWs.getCell(row, 2).value = r.scope ?? r.attendanceScope ?? "";
      cWs.getCell(row, 3).value = r.processed ?? r.attendanceProcessed ?? "";
      cWs.getCell(row, 4).value = r.open ?? r.attendanceUnprocessed ?? "";
      cWs.getCell(row, 5).value = r.locked ?? (r.attendanceMonthLocked ? "Yes" : "No");
      cWs.getCell(row, 6).value = r.presentRate ?? "";
      cWs.getCell(row, 7).value = r.totalAbsentDays ?? "";
    });
    cWs.getColumn(1).width = 22;
  }

  const stamp = new Date().toISOString().slice(0, 10);
  return downloadWorkbook(wb, `Attendance_Dashboard_${stamp}.xlsx`);
}
